import React, { useState, useEffect } from 'react';
import { ThreeScene } from './ThreeScene';
import { FloatingParticles } from './FloatingParticles';
import { AgentSwarm } from './Agent3D';
import { SampleTaskFlow3D } from './TaskFlow3D';
import { PerformanceMetrics3D } from './DataVisualization3D';

interface Dashboard3DBackgroundProps {
  showParticles?: boolean;
  showAgents?: boolean;
  showTaskFlow?: boolean;
  showMetrics?: boolean;
  intensity?: 'low' | 'medium' | 'high';
}

export function Dashboard3DBackground({
  showParticles = true,
  showAgents = true,
  showTaskFlow = true,
  showMetrics = true,
  intensity = 'medium'
}: Dashboard3DBackgroundProps) {
  const [currentScene, setCurrentScene] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  useEffect(() => {
    if (intensity === 'low') return;
    const interval = setInterval(() => {
      setCurrentScene((prev) => (prev + 1) % 4);
    }, intensity === 'high' ? 8000 : 15000);
    return () => clearInterval(interval);
  }, [intensity]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(!document.hidden);
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  if (!isVisible) return null;

  const getIntensitySettings = () => {
    switch (intensity) {
      case 'low':
        return { particleCount: 300, agentCount: 3, animationSpeed: 0.2 };
      case 'medium':
        return { particleCount: 600, agentCount: 5, animationSpeed: 0.4 };
      case 'high':
        return { particleCount: 1000, agentCount: 8, animationSpeed: 0.6 };
      default:
        return { particleCount: 600, agentCount: 5, animationSpeed: 0.4 };
    }
  };

  const settings = getIntensitySettings();

  const sampleAgents = Array.from({ length: settings.agentCount }, (_, i) => ({
    id: `agent-${i}`,
    type: ['box', 'sphere', 'torus', 'cylinder'][i % 4] as any,
    color: `hsl(${i * 60}, 70%, 60%)`,
    position: [
      (Math.random() - 0.5) * 8,
      (Math.random() - 0.5) * 4,
      (Math.random() - 0.5) * 6
    ] as [number, number, number]
  }));

  const samplePerformanceData = Array.from({ length: 6 }, (_, i) => ({
    id: `agent-${i}`,
    name: `Agent ${i + 1}`,
    performance: Math.random() * 100,
    tasksCompleted: Math.floor(Math.random() * 50) + 10,
    efficiency: Math.random() * 100
  }));

  return (
    <div className="fixed inset-0 pointer-events-none -z-10">
      <ThreeScene
        showParticles={showParticles}
        particleCount={settings.particleCount}
        particleColor="#6366f1"
        cameraPosition={[0, 0, 8]}
      >
        {showParticles && (
          <FloatingParticles
            count={settings.particleCount}
            color="#6366f1"
            speed={settings.animationSpeed}
          />
        )}

        {showAgents && (
          <AgentSwarm agents={sampleAgents} />
        )}

        {showTaskFlow && currentScene === 0 && (
          <SampleTaskFlow3D />
        )}

        {showMetrics && currentScene === 1 && (
          <PerformanceMetrics3D agents={samplePerformanceData} />
        )}
      </ThreeScene>
    </div>
  );
}

export function Simple3DBackground() {
  return (
    <Dashboard3DBackground
      showParticles={true}
      showAgents={false}
      showTaskFlow={false}
      showMetrics={false}
      intensity="low"
    />
  );
}

export function Rich3DBackground() {
  return (
    <Dashboard3DBackground
      showParticles={true}
      showAgents={true}
      showTaskFlow={true}
      showMetrics={true}
      intensity="high"
    />
  );
}
